import React from 'react';
import { Phone, Clock, MapPin, ChevronDown } from 'lucide-react';
import { BUSINESS } from '@/lib/businessData';
import { Image } from '@/components/ui/image';

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-background">
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <Image
          src="https://media.base44.com/images/public/6a5847080763d214450cfad8/554e076ec_generated_8c3282b5.png"
          alt="Heavy-duty garage door torsion spring macro detail — Mike's Garage Door Repair Cypress TX"
          fittingType="fill"
          className="block w-full h-full opacity-40"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        <div className="absolute inset-0 tech-grid opacity-50" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Headline */}
          <div>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 px-3 py-1.5 mb-6">
              <span className="w-2 h-2 bg-primary rounded-full pulse-amber" />
              <span className="font-mono text-xs text-primary uppercase tracking-wider">
                Tech Available Now — Cypress TX
              </span>
            </div>

            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-foreground text-balance">
              <span className="block text-primary">24/7 Garage Door Service and Repair</span>
            </h1>

            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Mike's Garage Door Repair delivers <strong className="text-foreground">emergency garage door repair in Cypress, TX</strong> —
              springs, openers, off-track doors, and panel replacement. Same-day dispatch, upfront pricing, 18 years local.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="flex items-center justify-center gap-2 bg-primary text-background px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:amber-glow transition-all"
                aria-label={`Call for emergency garage door repair: ${BUSINESS.phone}`}
              >
                <Phone className="w-5 h-5" />
                Call Now — {BUSINESS.phone}
              </a>
              <a
                href="#booking"
                className="flex items-center justify-center gap-2 border-2 border-border text-foreground px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:border-primary hover:text-primary transition-all"
              >
                Book Service
              </a>
            </div>

            {/* Quick stats */}
            <div className="mt-10 grid grid-cols-3 gap-4 pt-8 border-t border-border">
              <div>
                <div className="font-heading font-black text-2xl text-primary">30 min</div>
                <div className="font-mono text-[11px] text-muted-foreground uppercase mt-1">Avg Response</div>
              </div>
              <div>
                <div className="font-heading font-black text-2xl text-primary">{BUSINESS.yearsInBusiness} yrs</div>
                <div className="font-mono text-[11px] text-muted-foreground uppercase mt-1">Local Expertise</div>
              </div>
              <div>
                <div className="font-heading font-black text-2xl text-primary">{BUSINESS.rating}★</div>
                <div className="font-mono text-[11px] text-muted-foreground uppercase mt-1">{BUSINESS.reviewCount} Reviews</div>
              </div>
            </div>
          </div>

          {/* Right: Emergency Dispatch Card */}
          <div className="lg:justify-self-end w-full max-w-md">
            <div className="bg-card border border-border p-6 sm:p-8 relative">
              <div className="absolute -top-3 left-6 bg-primary text-background px-3 py-1 font-heading font-bold text-xs uppercase tracking-wider stencil">
                Emergency Dispatch
              </div>

              <div className="flex items-center gap-2 mb-4">
                <span className="w-3 h-3 bg-primary rounded-full pulse-amber" />
                <span className="font-mono text-sm text-primary uppercase tracking-wider">Live — Dispatching Now</span>
              </div>

              <h2 className="font-heading font-bold text-xl text-foreground mb-1">
                Garage Door Won't Open?
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                Mike's truck is loaded and ready. Cypress-wide 24/7 emergency response.
              </p>

              {/* Tech availability indicator */}
              <div className="bg-background/60 border border-border p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-xs text-muted-foreground uppercase">Technician Proximity</span>
                  <span className="font-heading font-bold text-sm text-primary">2.4 mi</span>
                </div>
                <div className="h-2 bg-background rounded-full overflow-hidden border border-border">
                  <div className="h-full w-[85%] bg-primary rounded-full" />
                </div>
                <div className="flex items-center gap-1.5 mt-3">
                  <MapPin className="w-3 h-3 text-primary" />
                  <span className="font-mono text-xs text-muted-foreground">Near Bridgeland Pkwy → En Route</span>
                </div>
              </div>

              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="flex items-center justify-center gap-2 w-full bg-primary text-background py-4 font-heading font-bold text-sm uppercase tracking-wider hover:amber-glow transition-all"
              >
                <Phone className="w-5 h-5" />
                {BUSINESS.phone}
              </a>

              <div className="flex items-center justify-center gap-2 mt-4">
                <Clock className="w-4 h-4 text-primary" />
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">{BUSINESS.hours}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <a
        href="#services"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-muted-foreground hover:text-primary transition-colors hidden md:block"
        aria-label="Scroll to services"
      >
        <ChevronDown className="w-6 h-6 animate-bounce" />
      </a>
    </section>
  );
}